import React, {useCallback, useState} from 'react';
import FileUploadCallDetailsActions from 'actions/files/FileUploadCallDetailsActions';
import './CrmSyncCloudIcon.styl';
import classNames from 'classnames';
import User from 'records/User';
import EvTooltip from '@components/tooltip/EvTooltip';
import {CloudIcon} from '@components/icon/CloudIcon';
import {CloudArrowIcon} from '@components/icon/CloudArrowIcon';

interface CrmSyncCloudIconProps {
  crmLink?: string;
  callId: string;
  caller: User;
  authUser: User;
}

// TODO is this accurate? stolen from the auth endpoints
const salesforceDomains = ['salesforce.com', 'force.com', 'lightning.com', 'visualforce.com'];

// we try to match this exactly - https://repo.execvision.io/dev/api/-/blob/develop/ev_api/crm/services/sync_crm_data.py#L95
const canSync = (authUser: User, caller: User) => {
  // explicitly has 2 way sync perms, can always sync
  if (authUser.permissions.includes('can_two_way_sync')) return true;

  // if they're the caller, can always sync
  if (authUser.id === caller.id) return true;

  // if they're the manager of the caller's team, they can sync
  if (authUser.teamId === caller.teamId && authUser.role === 'manager') return true;

  // note that admins fall through here if they're not managers of the callers team or they dont have the perm.
  return false;
};

const getRenderDetails = (authUserCanSync, linkIncludesSalesforce) => {
  if (authUserCanSync) {
    return {
      className: linkIncludesSalesforce ? null : 'can-sync',
      tooltip: linkIncludesSalesforce
        ? 'Click to view or change related Salesforce record'
        : 'Click to sync to Salesforce',
      interaction: (callId, _) =>
        linkIncludesSalesforce
          ? // this pops up the dialog that allows two actions - resync or view in salesforce
            FileUploadCallDetailsActions.showCRMRecordDialog(callId)
          : // this is the modal with only the sync options
            FileUploadCallDetailsActions.showCallSyncCRM(callId),
    };
  } else {
    return {
      className: linkIncludesSalesforce ? null : 'no-match',
      tooltip: linkIncludesSalesforce
        ? 'Click to view Salesforce record'
        : 'No match found. Caller or their manager can manually match',
      interaction: (_, crmLink) => (linkIncludesSalesforce ? window.open(crmLink, '_blank') : null),
    };
  }
};

/**
 * This cloud icon exists to allow customers with salesforce CRM integrations to view and update
 * this upstream info. See `supports_cloud` for more information.
 *
 * Note that it only supports salesforce (see usages/supports_cloud).
 *
 */
const CrmSyncCloudIcon: React.FC<CrmSyncCloudIconProps> = ({crmLink, callId, caller, authUser}) => {
  const [hover, setHover] = useState(false);
  const authUserCanSync = canSync(authUser, caller);
  const linkIncludesSalesforce = salesforceDomains.some(d => crmLink?.includes(d));

  const {className, tooltip, interaction} = getRenderDetails(
    authUserCanSync,
    linkIncludesSalesforce
  );

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      // prevent this from bubbling out to the call card -> navigate action
      e.stopPropagation();

      interaction(callId, crmLink);
    },
    [callId, crmLink, interaction]
  );

  return (
    <EvTooltip title={tooltip} arrow placement="top">
      <div
        className={classNames('salesforce-cloud-icon', className)}
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}>
        {hover ? <CloudArrowIcon /> : <CloudIcon />}
      </div>
    </EvTooltip>
  );
};

export default CrmSyncCloudIcon;
